/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-01 15:00:00
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as installActions from 'actions/install-actions';
import { Card, Button, Link, Icon } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import history from 'src/routes/history';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import { ExternalMinor } from '@shopify/polaris-icons';
import ThemeSetupForm from './theme-setup-form';

import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import WarningLiveThemeOnboardingModal from 'src/components/views/theme-setup-v3/theme-setup-form/warning-live-theme-onboarding-modal';
import WarningOS10Modal from 'src/components/views/theme-setup-v3/theme-setup-form/warning-os10-onboarding-modal';
import ContactUsModal from './contact-us-modal';
import { confirmLeavePage } from 'src/helpers';
import ConfirmLeaveModal from 'src/components/views/common/handle-leave/confirm-leave-modal';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import { isDirty } from 'redux-form';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import { routes } from 'src/routes';
import RedirectToDashboardModal from './redirect-to-dashboard-modal';
import { URL } from 'src/constants/links';
import { getFeatureByFeatureInterestKYC } from 'src/constants/installation-v2';
import * as syncActions from 'actions/sync-actions';
import { SYNC_TYPE_FLOW_SUB_FLOW } from 'src/constants/sync';
const TypeScreen = {
  List: 'List',
  Setup: 'Setup',
  Revert: 'Revert'
};

const PageType = {
  SetupForm: 'SetupForm',
  Success: 'Success',
  Unsuccess: 'Unsuccess'
};

export const ONBOARDING_V3_THEME_SETUP_DATA_KEY = 'OnboardingV3-ThemeSetup-Data';

type Props = WithI18nProps;

class ChooseThemeScreen extends Component<Props> {
  state = {
    toggleThemes: false,
    youtubeModal: false,
    step: 1,
    isNextableStep: false,
    isSetuppable: false,
    dataForm: {},
    activeModalWarningLiveTheme: false,
    activeModalWarningOS10: false,
    activeModalConfirmLeavePage: false,
    activeModalContactUs: false,
    activeModalRedirectToDashboard: false,
    isSettingUp: false,
    page: PageType.SetupForm,
    formData: {},
    isBackAfterSubmit: false,
    isDismissBannerComplete: false,
    themeSelected: null,
    alertedNotify: false,

    progressSync: 0,
    timeToCompleteText: '',
    isClickedSkip: false
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  onCheckNextableStep = (isNextableStep, isSetuppable, dataForm, formData, themeCheck) => {
    this.setState({
      isNextableStep,
      isSetuppable,
      dataForm,
      formData,
      themeSelected: themeCheck
    });
  };

  onNext = () => {
    const { step, dataForm = {} } = this.state;
    const { currentThemeSelected } = dataForm;

    if (currentThemeSelected && currentThemeSelected.role === 'main') {
      this.toggleChangeState('activeModalWarningLiveTheme');
    } else {
      this.setState({
        step: step + 1
      });
    }
  };

  handleClickCancel = () => {
    if (this.state.step === 2) {
      this.setState({
        step: 1,
        isNextableStep: true
      });
    } else {
      this.confirmLeave(TypeScreen.List);
    }
  };

  componentDidMount() {
    const { getRecommendationWidgets } = this.props;
    getRecommendationWidgets(true);
    this.processInstallStatus();

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { generalFormDirty, i18n } = this.props;
    return confirmLeavePage(event, generalFormDirty, i18n);
  };

  // Close modal
  closeModal = () => {
    this.setState({ activeModalConfirmLeavePage: false, actionToGo: () => {}, params: {} });
  };

  // Handle confirm leave modal
  // Discard change, continue action
  handleDiscard = () => {
    const { actionToGo } = this.state;
    actionToGo();

    // Close modal
    this.closeModal();
  };

  // Handle confirm leave
  confirmLeave = (action, params = {}) => {
    const { generalFormDirty, handleThemeSetupChangeScreen } = this.props;
    let callBack = (params = {}) => {};
    switch (action) {
      case TypeScreen.List: {
        callBack = () => handleThemeSetupChangeScreen(TypeScreen.List);
        break;
      }
      default:
    }
    if (generalFormDirty) {
      this.setState({ activeModalConfirmLeavePage: true, actionToGo: callBack, params: params });
    } else {
      // $FlowFixMe
      callBack(params);
    }
  };

  onSetup = () => {
    const { dataForm, themeSelected } = this.state;
    const {
      setupThemeV3,
      i18n,
      themeCheckCompatible,
      themeCheckCompatibleId,
      updateInstallationStatus,
      triggerSync
    } = this.props;

    const position = _.get(dataForm, 'currentPositionSelect', null);
    const features = _.get(dataForm, 'currentFeatureSelected', {}) || {};
    const widgets = _.get(dataForm, 'currentWidgetSelect', {}) || {};
    const recommendationWidgets = _.reduce(
      _.filter(_.keys(widgets), widget => widgets[widget]),
      (result, widgetId) => {
        if (widgetId.includes('collectionpage')) {
          if (!result['collection-page']) {
            result['collection-page'] = [];
          }
          result['collection-page'].push(widgetId);
        }
        if (widgetId.includes('cartpage')) {
          if (!result['cart-page']) {
            result['cart-page'] = [];
          }
          result['cart-page'].push(widgetId);
        }
        if (widgetId.includes('productpage')) {
          if (!result['product-page']) {
            result['product-page'] = [];
          }
          result['product-page'].push(widgetId);
        }
        if (widgetId.includes('homepage')) {
          if (!result['home-page']) {
            result['home-page'] = [];
          }
          result['home-page'].push(widgetId);
        }
        return result;
      },
      {}
    );

    let layout = '';
    if (position && position[0]) {
      layout = position[0] === 'v' ? 'vertical' : 'horizontal';
    } else {
      if (_.get(themeSelected, 'taeFeatures.filterCollection', '') === 'installed') {
        layout = _.get(themeSelected, 'taeFeatures.filterLayout.filterSearch', '');
      }
      if (_.get(themeSelected, 'taeFeatures.filterSearch', '') === 'installed') {
        layout = _.get(themeSelected, 'taeFeatures.filterLayout.filterCollection', '');
      }
    }

    const filterLayout = {
      collection: features?.filterCollection ? layout : '',
      search: features?.filterSearch ? layout : ''
    };

    const taeFeatures = {
      filterLayout,
      recommendationWidgets
    };

    if (features?.recommendation) {
      taeFeatures.recommendation = 'checked';
    }
    if (features?.instantSearch) {
      taeFeatures.instantSearch = 'checked';
    }
    if (features?.filterCollection) {
      taeFeatures.filterCollection = 'checked';
    }
    if (features?.filterSearch) {
      taeFeatures.filterSearch = 'checked';
    }

    const formData = {
      themeId: themeCheckCompatibleId,
      taeFeatures,
      isOnBoarding: true
    };

    if (themeCheckCompatible.taeFeatures) {
      if (_.get(themeCheckCompatible, 'taeFeatures.recommendation', '') === 'installed') {
        formData.taeFeatures.recommendation = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.instantSearch', '') === 'installed') {
        formData.taeFeatures.instantSearch = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterCollection', '') === 'installed') {
        formData.taeFeatures.filterCollection = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterSearch', '') === 'installed') {
        formData.taeFeatures.filterSearch = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterLayout.collection', '') !== '') {
        formData.taeFeatures.filterLayout.collection = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.collection',
          ''
        );
        formData.taeFeatures.filterLayout.search = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.collection',
          ''
        );
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterLayout.search', '') !== '') {
        formData.taeFeatures.filterLayout.collection = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.search',
          ''
        );
        formData.taeFeatures.filterLayout.search = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.search',
          ''
        );
      }
      if (
        themeCheckCompatible?.taeFeatures?.recommendationWidgets &&
        _.keys(themeCheckCompatible?.taeFeatures?.recommendationWidgets).length > 0
      ) {
        formData.taeFeatures.recommendationWidgets =
          themeCheckCompatible?.taeFeatures?.recommendationWidgets;
      }
    }

    if (!formData.taeFeatures.recommendation) {
      delete formData.taeFeatures.recommendationWidgets;
    }
    if (!formData.taeFeatures.filterCollection && !formData.taeFeatures.filterSearch) {
      delete formData.taeFeatures.filterLayout;
    }

    this.setState(
      {
        isSettingUp: true
      },
      () => {
        setTimeout(() => {
          history.push(routes.newInstallation.running());
        }, 1000);
        const data = {
          id: themeCheckCompatibleId,
          boostThemeLib: themeCheckCompatible.boostThemeLib,
          boostThemeLibVersion: themeCheckCompatible.boostThemeLibVersion,
          isLiveTheme: themeCheckCompatible.isLiveTheme,
          shopifyThemeName: themeCheckCompatible.shopifyThemeName,
          name: themeSelected.name,
          features
        };
        updateInstallationStatus('submitted-choose-theme');
        if (!themeCheckCompatible.isLiveTheme) {
          triggerSync(SYNC_TYPE_FLOW_SUB_FLOW);
        }
        this.saveThemeSetupDataToLocalStorage(data);
        setupThemeV3(formData, null, i18n);
      }
    );
  };

  handleContinueWarning = () => {
    const { step } = this.state;
    this.setState({
      step: step + 1,
      activeModalWarningLiveTheme: false
    });
  };

  handleContactWarning = () => {
    this.toggleChangeState('activeModalWarningOS10');
    this.toggleChangeState('activeModalContactUs');
  };

  processInstallStatus = async () => {
    return;
    // const { getInstallStatus } = this.props;
    // const installStatusResponse = await getInstallStatus();
    // const syncStatusInfo = _.get(installStatusResponse, 'payload.data.syncStatus', {});
    // const _syncInfoStartAt = _.get(syncStatusInfo, 'startedAt');
    // const _syncInfoProgress = _.get(syncStatusInfo, 'progress');
    // const _syncInfoLastSyncedDoneAt = _.get(syncStatusInfo, 'lastSynced.doneAt');
    // const timeToCompleteText = getMinuteToCompleteText(
    //   _syncInfoStartAt,
    //   _syncInfoLastSyncedDoneAt,
    //   _syncInfoProgress
    // );
    // this.setState({
    //   progressSync: Number(syncStatusInfo.progress * 100)
    //     .toFixed(1)
    //     .replace('.0', ''),
    //   timeToCompleteText
    // });
  };

  componentWillReceiveProps(nextProps) {
    const { syncInfoStatus, syncInfoProgress } = nextProps;

    if (syncInfoStatus !== 'done' && syncInfoProgress !== 1 && nextProps.loading === false) {
      setTimeout(async () => {
        await this.processInstallStatus();
      }, 5000);
    }
  }

  saveThemeSetupDataToLocalStorage(data) {
    const { tenantApp } = this.props;
    try {
      localStorage.setItem(
        `${tenantApp?.tenantId}-${ONBOARDING_V3_THEME_SETUP_DATA_KEY}`,
        JSON.stringify(data)
      );
    } catch (error) {}
  }

  handleSubmitRequest = () => {
    this.setState({
      activeModalContactUs: false,
      activeModalRedirectToDashboard: true
    });
    setTimeout(() => {
      history.push(routes.dashboard.view());
    }, 5000);
  };

  onSkip = () => {
    this.setState({ isClickedSkip: true });
    history.push(routes.dashboard.view());
  };

  onCheckLeavePage = path => {
    if (path === routes.dashboard.view() && this.state.isClickedSkip) {
      const { updateInstallationStatus } = this.props;
      updateInstallationStatus('');
    }
    history.push(path);
  };

  render() {
    const {
      isNextableStep,
      step,
      isSetuppable,
      activeModalWarningLiveTheme,
      activeModalWarningOS10,
      activeModalConfirmLeavePage,
      activeModalContactUs,
      activeModalRedirectToDashboard,
      isSettingUp,
      formData,
      isBackAfterSubmit
    } = this.state;
    const {
      i18n,
      generalFormDirty,
      themeSelected,
      checkThemeLoading,
      features,
      KYCValueFeatures,
      tenantApp
    } = this.props;
    const isOldUser = tenantApp.firstInstalledAt !== tenantApp.recentInstalledAt;

    return (
      <div className="BC-AnotherLayout BC-page_Installation ChooseTheme">
        <Card>
          <div
            className={`BC-page_Installation--ChooseTheme ${
              activeModalRedirectToDashboard ? 'ModalRedirect' : ''
            }`}>
            <div className="wrapper">
              <div className="content-top">
                <div className="info">
                  <div className="title">
                    {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.title_Setup')}
                  </div>
                  <div className="description">
                    {i18n.translate('AdminHTML.ThemeSetup.Onboarding.theme_setup_description')}
                  </div>
                </div>
              </div>

              <div className="content-main">
                <ThemeSetupForm
                  themeSelected={themeSelected}
                  formData={formData}
                  isBackAfterSubmit={isBackAfterSubmit}
                  onCheckNextableStep={this.onCheckNextableStep}
                  step={step}
                  featuresCheckedAsDefault={getFeatureByFeatureInterestKYC(
                    features?.length > 0 ? features : KYCValueFeatures,
                    isOldUser
                  )}
                  backStep={this.handleClickCancel}></ThemeSetupForm>
              </div>

              <div className="content-bottom">
                <div className="description">
                  {i18n.translate('AdminHTML.ThemeSetup.Onboarding.theme_setup_guide', {
                    i: (
                      <i>
                        <Link external url={URL['OnboardingThemeSetupTAE']}>
                          {i18n.translate('AdminHTML.ThemeSetup.Onboarding.theme_setup_guide_i')}
                        </Link>
                      </i>
                    ),
                    link: (
                      <span className="img-new-tab">
                        <Link external url={URL['OnboardingThemeSetupGuide']}>
                          {i18n.translate('AdminHTML.ThemeSetup.Onboarding.theme_setup_guide_link')}
                          <Icon source={ExternalMinor} color="interactive" />
                        </Link>
                      </span>
                    )
                  })}
                </div>
                <div className="action">
                  <div className="theme-setup-menu-action">
                    {step === 1 && (
                      <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                        {isOldUser && (
                          <span
                            style={{ color: 'rgba(109, 113, 117, 1)', cursor: 'pointer' }}
                            onClick={this.onSkip}>
                            {i18n.translate('AdminHTML.Common.Action.skip')}
                          </span>
                        )}
                        <Button
                          primary
                          disabled={checkThemeLoading || (isNextableStep ? false : true)}
                          onClick={this.onNext}>
                          {i18n.translate('AdminHTML.Common.Action.next')}
                        </Button>
                      </div>
                    )}
                    {step === 2 && (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Button disabled={isSettingUp} onClick={this.handleClickCancel}>
                          {i18n.translate('AdminHTML.Common.Action.back')}
                        </Button>
                        <Button
                          loading={isSettingUp}
                          primary
                          disabled={isSetuppable ? false : true}
                          onClick={this.onSetup}>
                          {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step3_BtnRunSetup')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <WarningLiveThemeOnboardingModal
            active={activeModalWarningLiveTheme}
            handleBack={() => this.toggleChangeState('activeModalWarningLiveTheme')}
            handleContinue={this.handleContinueWarning}
          />
          <WarningOS10Modal
            isChooseText={true}
            active={activeModalWarningOS10}
            handleSelectAnother={() => this.toggleChangeState('activeModalWarningOS10')}
            handleContact={this.handleContactWarning}
          />
          <ContactUsModal
            active={activeModalContactUs}
            handleCancel={() => this.toggleChangeState('activeModalContactUs')}
            handleSubmitRequest={this.handleSubmitRequest}
          />
          <RedirectToDashboardModal active={activeModalRedirectToDashboard} />
          {/* This modal is used for when click any element on this page, but route not change */}
          <ConfirmLeaveModal
            active={activeModalConfirmLeavePage}
            handleCancel={this.closeModal}
            handleDiscard={this.handleDiscard}
          />
          {/* This modal is used for when click any link on this page, the route will change */}
          {!(isSettingUp || activeModalRedirectToDashboard) && (
            <RouteLeavingGuard
              when={generalFormDirty}
              navigate={this.onCheckLeavePage}
              shouldBlockNavigation={generalFormDirty}
            />
          )}
        </Card>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { themeSetupV3 = {} } = state;
  const generalFormDirty = isDirty('ThemeSetup')(state);
  const setupThemeSuccess = _.get(state.themeSetupV3, 'setupThemeSuccess', undefined);

  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts', 0);

  return {
    generalFormDirty,
    setupThemeSuccess,
    checkThemeLoading: _.get(themeSetupV3, 'checkThemeLoading', false),
    tenantApp: _.get(state, 'oauth.tenantApp', null),
    themeCheckCompatible: _.get(themeSetupV3, 'themeCheck', null),
    themeCheckCompatibleId: _.get(themeSetupV3, 'themeCheckId', null),

    currentTotalProducts,
    loading: state.install.loading,
    installStatus: state.install.installStatus,
    installSyncPercent: state.install.installSyncPercent,
    installSetupThemePercent: state.install.installSetupThemePercent,
    installStep: state.install.installStep,
    installTotal: state.install.installTotal,
    installCurrentStep: state.install.installCurrentStep,
    installThemeId: state.install.installThemeId,
    installThemeName: state.install.installThemeName,
    installThemeUpdatedAt: state.install.installThemeUpdatedAt,

    syncInfoStartAt: state.install.syncStartedAt,
    syncInfoStatus: state.install.syncStatus,
    syncInfoProgress: state.install.syncProgress,
    syncInfoLastSyncedStatus: state.install.syncInfoLastSyncedStatus,
    syncInfoLastSyncedDoneAt: state.install.syncInfoLastSyncedDoneAt,
    // KYC features: get from connect
    features: _.get(state, 'oauth.tenantApp.kycData.features', []),
    // KYC features: get from store
    KYCValueFeatures: _.get(state, 'install.KYCValue.features', [])
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  getRecommendationWidgets: isOnBoarding =>
    dispatch(recommendationAction.getRecommendationWidgets(ownProps.i18n, isOnBoarding)),
  setupThemeV3: (body: Object) => dispatch(themeSetupV3Action.setupThemeV3(body, ownProps.i18n)),
  getInstallStatus: () =>
    dispatch(installActions.getInstallStatus(ownProps.i18n, { queries: { t: 't-choose-theme' } })),
  updateInstallationStatus: status => dispatch(installActions.updateInstallationStatus(status)),
  triggerSync: flowName => dispatch(syncActions.triggerSync(flowName, ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ChooseThemeScreen));
